import React from 'react';
import { Tooltip, Flex, Image, Table, Tag, message, Divider } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import SetupSensorIcon from '../../../assets/images/SetupSensorIcon.jsx';
import { UpdateSingleAlert, type AlertGroup } from '@/apis/settings';
import type { AlertConfigData, FeaturePermissionMatrix } from '@/apis/types';
// icons / images
import {
  warehouseIcon,
  preflightIcon,
  postFlightIcon,
  deliveryIcon,
  monitoringIcon,
  exceptionIcon,
  otherIcon,
} from '../../../assets/images';
import {
  OffIcon,
  SignalIcon,
  SensorNotConnectedIcon,
} from '../../../assets/icons/icons.jsx';
// helpers
import { capitalize, sentenceCase, useToken } from '../../../common/helper';
import { getAlertDisplayName } from '@/common/alertType';
// components
import {
  ToggleSwitch,
  CustomTitle,
  CustomShipmentText,
} from '../../../components';
import styles from './AlertCustomizationInput.module.scss';

interface TeamOption {
  id?: string;
  name?: string;
}

interface AlertCustomizationInputProps {
  group: AlertGroup;
  teamsOptions: TeamOption[];
  navigate: (path: string) => void;
  selectedTeam: string;
  handleSelectedAlertData: (alert: AlertConfigData) => void;
  refreshAlerts: () => void;
  AlertCustomizationPermissions?: FeaturePermissionMatrix;
}

/**
 * Displays a table of alert configurations grouped by shipment phase
 *
 * @param group - Alert group with phase and alerts data
 * @param teamsOptions - Available team options
 * @param navigate - Navigation function
 * @param selectedTeam - Currently selected team(s)
 * @param handleSelectedAlertData - Callback when alert row is clicked
 * @param refreshAlerts - Callback to refresh alerts data
 * @param AlertCustomizationPermissions - User permissions for alert customization
 */

const tagBaseStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontWeight: 600,
  border: 'none',
  borderRadius: 4,
  padding: '2px 4px',
  fontSize: 10,
  lineHeight: '16px',
  margin: 0,
};

const tagEnabledStyle: React.CSSProperties = {
  ...tagBaseStyle,
  backgroundColor: 'var(--thin-green)',
  color: '#166C3B',
};

const tagOffStyle: React.CSSProperties = {
  ...tagBaseStyle,
  backgroundColor: 'var(--danger-thin)',
  color: '#BB032A',
};

const tagNeutralStyle: React.CSSProperties = {
  ...tagBaseStyle,
  backgroundColor: 'var(--light-gray)',
  color: 'var(--text-secondary)',
};

const AlertCustomizationInput: React.FC<AlertCustomizationInputProps> = ({
  group,
  teamsOptions,
  navigate,
  selectedTeam,
  handleSelectedAlertData,
  refreshAlerts,
  AlertCustomizationPermissions,
}) => {
  const { token, removeToken } = useToken();

  const shouldDisableReset =
    selectedTeam?.length === 0 && teamsOptions.length !== 0;

  const getImageByStageName = (name: string): string | null => {
    const imageMap: Record<string, string> = {
      PRE_PICK: warehouseIcon,
      PRE_FLIGHT: preflightIcon,
      POST_FLIGHT: postFlightIcon,
      AT_DELIVERY: deliveryIcon,
      CONTINUOUS: monitoringIcon,
      TRANSPORT_PROVIDER_EXCEPTIONS: exceptionIcon,
      OTHER: otherIcon,
    };

    return imageMap[name] || null;
  };

  // ***NOTE***//
  /////////////////////////////////////////
  // rule.sensor_status == "setup_required" TO SHOW SETUP SENSOR BUTTON
  // rule.showSetupSensorButton TO SHOW SETUP SENSOR BUTTON
  // rule.sensor_required == "optional" TO SHOW SENSOR OPTIONAL TAG
  // rule.is_critical_type TO SHOW CIRCLE INFO ICON
  /////////////////////////////////////////

  const unauthorize = (status: number): void => {
    if (status === 401) {
      removeToken();
      navigate('/login');
    }
  };

  const handleToggle = (
    e: React.ChangeEvent<HTMLInputElement>,
    data: AlertConfigData
  ): void => {
    if (data?.alert_type === 'transport_provider_exception') return;
    const model = {
      team: selectedTeam || '',
      all_team: !selectedTeam || selectedTeam === 'all',
      alert_type: data?.alert_type,
      is_enabled: e.target.checked,
    };

    UpdateSingleAlert(model, token ?? '')
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            message.success(data?.data?.message);
            refreshAlerts();
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      });
  };

  const columns: ColumnsType<AlertConfigData> = [
    {
      title: 'Alert type',
      dataIndex: 'title',
      width: 280,
      minWidth: 230,
      key: 'title',
      render: (text, rule) => (
        <Flex gap={8} vertical>
          <div className={styles.alertTitle}>
            {sentenceCase(
              getAlertDisplayName(rule?.alert_type, rule?.alert_name)
            )}
          </div>

          <Flex wrap gap={8} align='center' vertical={false}>
            {rule.sensor_status !== 'setup_required' && (
              <>
                {/* Case 1: sensor_required == 'required' */}
                {rule.sensor_required === 'required' && (
                  <Tag
                    icon={rule.is_enabled ? <SignalIcon /> : <OffIcon />}
                    style={rule.is_enabled ? tagEnabledStyle : tagOffStyle}
                  >
                    {rule.is_enabled ? 'Enabled' : 'Off'}
                  </Tag>
                )}

                {/* Case 2: sensor_required == 'none' AND is_enabled is false */}
                {rule.sensor_required === 'none' &&
                  rule.is_enabled === false && (
                    <Tag icon={<OffIcon />} style={tagOffStyle}>
                      Off
                    </Tag>
                  )}
              </>
            )}

            {rule?.sensor_required === 'optional' && (
              <div className={styles.sensorOptionalWrapper}>
                <Tag style={{ ...tagNeutralStyle, position: 'absolute' }}>
                  Sensor optional
                </Tag>
              </div>
            )}
            {rule.sensor_status === 'setup_required' && (
              <Tooltip title='Sensor required: The correct sensor must be connected to enable this alert'>
                <Tag icon={<SensorNotConnectedIcon />} style={tagNeutralStyle}>
                  Not connected
                </Tag>
              </Tooltip>
            )}

            {rule.is_critical_type && (
              <div className={styles.criticalIconWrapper}>
                <Tooltip title='Critical alert: An alert that is indicative of a potential product compromise'>
                  <SetupSensorIcon className='svg-icon gray-icon hover-red' />
                </Tooltip>
              </div>
            )}
          </Flex>
        </Flex>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 450,
      render: (text, rule) => (
        <span
          className={`${!rule?.is_enabled && 'disabled-row'} ${styles.smallText}`}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Threshold',
      key: 'threshold',
      width: 145,
      render: (_, rule) => {
        const hasMultipleThresholds = Boolean(
          rule?.threshold_config && rule.threshold_config.length > 1
        );

        const thresholdText =
          rule?.alert_type === 'vehicle_idle_in_transit'
            ? `${capitalize(rule?.threshold_comparison)} ${Number(rule?.threshold_value) / 60} hrs`
            : rule?.threshold_display;

        const countBadge =
          hasMultipleThresholds && rule?.threshold_config ? (
            <Tooltip
              placement='top'
              color='var(--surface-elevated)'
              styles={{
                container: {
                  width: 'max-content',
                  maxWidth: 'none',
                  padding: '0px',
                  borderRadius: '8px',
                  boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
                  border: '0px',
                },
              }}
              title={
                <div className={styles.thresholdTooltip}>
                  <div className={styles.thresholdTooltipHeader}>
                    <CustomShipmentText text={'Threshold'} fontWeight={600} />
                  </div>
                  <Divider className='notification-divider' />
                  <Flex vertical gap={4} style={{ padding: '12px' }}>
                    {rule.threshold_config.map((item, index) => (
                      <Flex
                        key={item.key || index}
                        justify='space-between'
                        align='center'
                        gap={16}
                      >
                        <CustomShipmentText
                          text={item.label}
                          fontWeight={600}
                          whiteSpace='nowrap'
                        />
                        <CustomShipmentText
                          text={item.display_text}
                          fontSize='10px'
                          whiteSpace='nowrap'
                        />
                      </Flex>
                    ))}
                  </Flex>
                </div>
              }
            >
              <span className={styles.countTag}>+{rule?.threshold_count}</span>
            </Tooltip>
          ) : null;

        return (
          <Flex align='center' justify='space-between' vertical={false} gap={6}>
            <CustomTitle
              text={thresholdText}
              fontSize={12}
              fontWeight='400'
              lineHeight='20px'
              color='var(--text-primary)'
              marginBottom={0}
              className={
                rule?.sensor_status === 'setup_required' || !rule?.is_enabled
                  ? 'disabled-row'
                  : ''
              }
            />
            {countBadge}
          </Flex>
        );
      },
    },
    {
      title: 'Contact',
      dataIndex: 'contacts',
      key: 'contacts',
      width: 160,
      render: (text, rule) => (
        <>
          <span
            className={`${!rule?.is_enabled && 'disabled-row'} ${styles.smallText}`}
          >
            {rule?.recommended_contacts
              ?.map((d) => (d.name ?? '').replace(/ primary contact$/i, ''))
              .join(', ')}
          </span>
        </>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 90,
      render: (_, rule) => (
        <>
          <div className={styles.actions}>
            {rule.sensor_status === 'setup_required' ? (
              <div
                onClick={() => {
                  navigate('/settings/sensor');
                }}
                className={styles.setupSensorButton}
              >
                Setup sensor
              </div>
            ) : (
              <>
                {AlertCustomizationPermissions?.is_create ||
                AlertCustomizationPermissions?.is_update ? (
                  <>
                    <div
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <ToggleSwitch
                        labelStyle={'alert-customization-bg'}
                        label=''
                        checked={rule?.is_enabled}
                        onChange={(e) => handleToggle(e, rule)}
                      />
                    </div>

                    <button
                      className={`hoverable-icon ${
                        rule.alert_type === 'transport_provider_exception'
                          ? styles.editButtonDisabled
                          : styles.editButton
                      }`}
                    >
                      <svg
                        onClick={() => handleSelectedAlertData(rule)}
                        className={`${rule.alert_type === 'transport_provider_exception' || shouldDisableReset ? 'disabled-row' : ''}`}
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                      >
                        <path
                          d='M6.47 4L5.53 4.94L8.58333 8L5.53 11.06L6.47 12L10.47 8L6.47 4Z'
                          fill='var(--text-primary)'
                        />
                      </svg>
                    </button>
                  </>
                ) : null}
              </>
            )}
          </div>
        </>
      ),
    },
  ];

  return (
    <div className={styles.group}>
      <Flex align='center' gap={8} style={{ marginBottom: 8 }}>
        <Image
          src={getImageByStageName(group.phase) ?? undefined}
          className='zl-invert-on-dark'
          width={28}
          height={28}
          preview={false}
        />
        <CustomTitle
          text={group.phase_display}
          fontSize={16}
          fontWeight='600'
          lineHeight='20px'
          color='var(--text-primary)'
          marginBottom={0}
        />
      </Flex>
      <div className='settings-content-wrapper'>
        <Table
          columns={columns}
          dataSource={group.alerts.map((r) => ({ ...r, key: r.id }))}
          onRow={(record) => {
            const isDisabled =
              record?.alert_type === 'transport_provider_exception' ||
              shouldDisableReset ||
              record?.sensor_status === 'setup_required' ||
              !(
                AlertCustomizationPermissions?.is_create ||
                AlertCustomizationPermissions?.is_update
              );

            return {
              onClick: () => {
                if (!isDisabled) {
                  handleSelectedAlertData(record);
                }
              },
              style: isDisabled
                ? { cursor: 'not-allowed' }
                : { cursor: 'pointer' },
            };
          }}
          pagination={false}
          rowHoverable={false}
          size='small'
          className='settings-table'
          rowClassName={(_, index) =>
            index === group.alerts?.length - 1 ? 'no-bottom-border' : ''
          }
        />
      </div>
    </div>
  );
};

export default AlertCustomizationInput;
